// @flow
import * as React from 'react';
import cx from 'classnames';

import {
  modifierClassName,
  type AllowedModifier
} from './fontAwesomeIconInternalModules/modifiers';
export type { AllowedModifier } from './fontAwesomeIconInternalModules/modifiers';
import {
  type Icon,
  assertIcon,
  iconFamilyClass
} from './fontAwesomeIconInternalModules/internalIconsModule';
export * from './fontAwesomeIconInternalModules/internalIconsModule';

export type Props = {|
  className?: string,
  icon: Icon,
  /**
   * A single or a list of modifiers without `fa-` prefix from one of these:
   *   - [Sizing Icons](https://docs.fontawesome.com/web/style/size)
   *   - [Fixed Width Icons](https://docs.fontawesome.com/web/style/fixed-width)
   *   - [Rotate Icons](https://docs.fontawesome.com/web/style/rotate)
   *   - [Animating Icons](https://docs.fontawesome.com/web/style/animate)
   *   - [Bordered & Pulled Icons](https://docs.fontawesome.com/web/style/pull)
   *   - [Accessibility-Minded Style Utilities](https://docs.fontawesome.com/web/dig-deeper/accessibility#accessibility-minded-style-utilities)
   */
  modifier?: AllowedModifier | $ReadOnlyArray<AllowedModifier>,
  style?: { ... },
  title?: string,
  'aria-label'?: string,
  'aria-hidden'?: boolean,
  role?: string,
  onClick?: () => mixed
|};

export default function FontAwesomeIcon(props: Props): React.Node {
  const {
    icon,
    className,
    modifier,
    'aria-hidden': ariaHiddenProp,
    ...passThroughProps
  } = props;
  const [iconFamily, iconName] = assertIcon(icon);

  const possibleModifiers: $ReadOnlyArray<AllowedModifier> = (() => {
    if (!modifier) return [];
    if (Array.isArray(modifier)) return modifier;
    return [modifier];
  })();

  const classNameForIcon = cx(
    iconFamilyClass(iconFamily),
    `fa-${iconName}`,
    possibleModifiers.map(mod => modifierClassName(mod)),
    className
  );

  const ariaHidden =
    typeof ariaHiddenProp === 'boolean' ? ariaHiddenProp : true;

  return (
    // eslint-disable-next-line rulesdir/no-plain-icons -- This is the safe call.
    <i
      {...passThroughProps}
      aria-hidden={ariaHidden}
      className={classNameForIcon}
    />
  );
}

export type StackProps = {|
  foregroundIcon: Icon,
  backgroundIcon: Icon
|};

export function FontAwesomeIconStack(props: StackProps): React.Node {
  return (
    <FontAwesomeIconStackWrapper>
      <FontAwesomeIcon
        icon={props.backgroundIcon}
        className={modifierClassName('stack-2x')}
      />
      <FontAwesomeIcon
        icon={props.foregroundIcon}
        className={modifierClassName('stack-1x')}
      />
    </FontAwesomeIconStackWrapper>
  );
}

export function FontAwesomeIconStackWrapper({
  children
}: {|
  children: React.Node
|}): React.Node {
  return <span className="fa-stack">{children}</span>;
}
